import { Injector, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { DocumentsErrorCodes, DocumentsSuccessCodes } from '@app/translations';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ButtonActionType,
  DocumentActionCategoryType,
  DocumentActionType,
  FileExt,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { ConfirmDialogService } from '@app/finxone-web-frontend/app/Modules/shared/components-library/confirmation-dilog/confirmation-dialog.service';
import { DocumentService } from '../../../services/document-service/document.service';
import { ProfileState } from '../../../state/user-profile.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { handleFileUpload } from '../document-actions';
import { showUploadFileConfirmDialog } from '../upload-confirm-dialog.utils';
import { uploadFileViaFlutterWebView } from '@app/finxone-web-frontend/app/lib/utils/upload-file-utils/flutter-file-upload.utils';
import {
  isFileSizeWithinLimit,
  MAX_UPLOAD_FILE_SIZE_BYTES,
} from '@app/finxone-web-frontend/app/lib/utils/upload-file-utils/file-size-validation.utils';

export async function secureDocumentUploadAction(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const flutterWebView = (window as any).flutter_inappwebview;
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const router = injector.get(Router);
  const documentService = injector.get(DocumentService);
  const viewContainerRef = injector.get(ViewContainerRef);
  const confirmDialogService = injector.get(ConfirmDialogService);
  const userId = store.selectSnapshot(ProfileState.getProfile)?.id;
  const buttonActionType = attri?.widgetProperties?.['buttonActionType'] as ButtonActionType;

  const flutterRequestData = {
    fileType: [FileExt.JPEG, FileExt.JPG, FileExt.SVG, FileExt.PNG, FileExt.GIF, FileExt.WEBP, FileExt.PDF],
    maxFileSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    usePhotoGallery: true,
    maxFile: 1,
    type: DocumentActionType.CUSTOMER_DOCUMENTS,
    entityId: userId,
    actionType: buttonActionType,
    category: DocumentActionCategoryType.CUSTOMER_DOCUMENTS,
  };

  // Use Flutter handler to access native file picker and photo gallery
  if (flutterWebView) {
    await uploadFileViaFlutterWebView(
      flutterWebView,
      flutterRequestData,
      confirmDialogService,
      store,
      router,
      alertHandlerService,
      () => {
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          DocumentsSuccessCodes.DOCUMENT_UPLOAD_SUCCESS,
        ]);
      },
    );
    return;
  }

  uploadDocumentViaWeb(
    userId,
    documentService,
    viewContainerRef,
    confirmDialogService,
    store,
    router,
    alertHandlerService,
  );
}

function uploadDocumentViaWeb(
  userId: string | undefined,
  documentService: DocumentService,
  viewContainerRef: ViewContainerRef,
  confirmDialogService: ConfirmDialogService,
  store: Store,
  router: Router,
  alertHandlerService: AlertHandlerService,
): void {
  const fileType = '.jpeg, .jpg, .png, .svg, .gif, .webp, .pdf';

  handleFileUpload(fileType, (file: File | null) =>
    handleUploadedFile(
      file,
      userId,
      documentService,
      viewContainerRef,
      confirmDialogService,
      store,
      router,
      alertHandlerService,
    ),
  );
}

function handleUploadedFile(
  file: File | null,
  userId: string | undefined,
  documentService: DocumentService,
  viewContainerRef: ViewContainerRef,
  confirmDialogService: ConfirmDialogService,
  store: Store,
  router: Router,
  alertHandlerService: AlertHandlerService,
): void {
  if (!file) return;

  if (!isFileSizeWithinLimit(file, store, router, alertHandlerService)) return;

  showUploadFileConfirmDialog(viewContainerRef, confirmDialogService, file.name).then(
    (confirmed: boolean) => {
      if (!confirmed) return;

      const formData = createFormData(file, userId);

      documentService.uploadDocument(formData).subscribe({
        next: () => {
          findAndShowAlertFromCode(store, router, alertHandlerService, [
            DocumentsSuccessCodes.DOCUMENT_UPLOAD_SUCCESS,
          ]);
        },
        error: (err: any) => {
          console.error('Upload Error:', err);
          findAndShowAlertFromCode(store, router, alertHandlerService, [
            DocumentsErrorCodes.DOCUMENT_UPLOAD_FAILED,
          ]);
        },
      });
    },
  );
}

/**
 * Creates FormData for document upload
 */
function createFormData(file: File, userId?: string): FormData {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('entityId', userId || '');
  formData.append('type', DocumentActionType.CUSTOMER_DOCUMENTS);
  formData.append('category', DocumentActionCategoryType.CUSTOMER_DOCUMENTS);
  return formData;
}
